/**
 * SiteGrantPanel — « Gardes et remplacements » : l'autorité de gestion DATÉE
 * (lot 3 §3.2, D3=b, « le manager de garde »).
 *
 * Source : `siteGrantsAPI` (`api/siteGrants.js`) →
 * `backend/routes/site_grant_routes.mjs` → entité `site_management_grant`.
 *
 * Ce panneau NE REMPLACE PAS `SiteAccessPanel` (le périmètre stable, 3.1) : il
 * s'y AJOUTE, second bloc du même onglet « Responsables ». Une garde datée porte
 * une fenêtre `valid_from`/`valid_to` — un manager de garde le week-end, un
 * remplaçant pendant une absence — sans toucher à l'organigramme.
 *
 * Ce que le panneau tient :
 *   - trois seaux (courantes, à venir, passées/révoquées) à partir du
 *     `computed_status` que le serveur calcule (jamais recalculé côté client :
 *     « maintenant » ne doit avoir qu'une seule horloge, celle du serveur) ;
 *   - un formulaire « Confier la garde à… » (utilisateur, du/au, motif) ;
 *   - une révocation anticipée, par garde ;
 *   - le bandeau « prend effet à la prochaine connexion » (même limite que 3.1,
 *     dite ici, pas cachée) ;
 *   - les refus serveur (périmètre, fenêtre invalide) affichés tels quels.
 *
 * 📌 MONTAGE : ce composant n'est pas monté par ce lot. Il attend un `siteId` et
 * rien d'autre — l'intégrateur le place dans l'onglet « Responsables » de
 * `SiteDetailModal.jsx`, à côté de `SiteAccessPanel` (3.1).
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Clock, UserPlus, ShieldOff, AlertTriangle } from 'lucide-react';
import { siteGrantsAPI } from '../../api/siteGrants.js';
import { userAPI } from '../../api/admin.js';
import { useFormat } from '../../i18n/format.js';

/** Un input datetime-local ne connait pas les fuseaux : patron de PoolReserveForDriverDialog.jsx. */
function toLocalInputValue(date) {
  const pad = (n) => String(n).padStart(2, '0');
  // Concaténation plutôt qu'un gabarit : la gate i18n (regex, pas parseur) se perd sur `${` et
  // prend le texte jusqu'au prochain accent grave pour un littéral (mesuré le 2026-09-15).
  return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate()) + 'T' + pad(date.getHours()) + ':' + pad(date.getMinutes());
}
function fromLocalInputValue(value) {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function defaultFrom() {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return d;
}
function defaultTo(from) {
  const d = new Date(from);
  d.setDate(d.getDate() + 7); // une semaine de garde par défaut — le formulaire reste libre
  return d;
}

const userLabel = (u) => [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.email || `#${u.id}`;

const BUCKET_ORDER = ['active', 'upcoming', 'past', 'revoked'];

function SiteGrantPanel({ siteId }) {
  const { t } = useTranslation();
  const { fmt } = useFormat();
  const queryClient = useQueryClient();

  const [formOpen, setFormOpen] = useState(false);
  const [userId, setUserId] = useState('');
  const [from, setFrom] = useState(() => toLocalInputValue(defaultFrom()));
  const [to, setTo] = useState(() => toLocalInputValue(defaultTo(defaultFrom())));
  const [reason, setReason] = useState('');
  const [formError, setFormError] = useState(null);

  const grantsQuery = useQuery({
    queryKey: ['siteGrants', siteId],
    queryFn: () => siteGrantsAPI.list(siteId),
    enabled: siteId != null,
  });

  const usersQuery = useQuery({
    queryKey: ['usersForGrant'],
    queryFn: () => userAPI.getAll(),
    enabled: formOpen,
  });

  const createMutation = useMutation({
    mutationFn: (payload) => siteGrantsAPI.create(siteId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['siteGrants', siteId] });
      setFormOpen(false);
      setUserId('');
      setReason('');
      setFormError(null);
    },
    onError: (err) => setFormError(err?.message || t('sites.grants.form.genericError')),
  });

  const revokeMutation = useMutation({
    mutationFn: (grantId) => siteGrantsAPI.revoke(grantId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['siteGrants', siteId] }),
  });

  const grants = grantsQuery.data?.data || [];
  const users = usersQuery.data?.data || [];

  const buckets = { active: [], upcoming: [], past: [], revoked: [] };
  for (const g of grants) {
    const key = BUCKET_ORDER.includes(g.computed_status) ? g.computed_status : 'past';
    buckets[key].push(g);
  }

  const submit = (e) => {
    e.preventDefault();
    setFormError(null);
    const uid = Number(userId);
    const fromIso = fromLocalInputValue(from);
    const toIso = fromLocalInputValue(to);
    if (!Number.isFinite(uid) || !userId) {
      setFormError(t('sites.grants.form.errors.missingUser'));
      return;
    }
    if (!fromIso || !toIso) {
      setFormError(t('sites.grants.form.errors.invalidWindow'));
      return;
    }
    if (Date.parse(fromIso) >= Date.parse(toIso)) {
      setFormError(t('sites.grants.form.errors.orderWindow'));
      return;
    }
    createMutation.mutate({ user_id: uid, valid_from: fromIso, valid_to: toIso, reason: reason || undefined });
  };

  const renderGrant = (g) => (
    <li
      key={g.id}
      className="flex items-start justify-between gap-3 py-2 border-b border-gray-100 last:border-b-0"
      data-testid={`site-grant-${g.id}`}
    >
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900">{g.user_name || `#${g.user_id}`}</p>
        <p className="text-xs text-gray-500">
          {t('sites.grants.window', { from: fmt.dateTime(g.valid_from), to: fmt.dateTime(g.valid_to) })}
        </p>
        {g.reason && <p className="text-xs text-gray-400 italic">{g.reason}</p>}
      </div>
      {g.computed_status === 'active' || g.computed_status === 'upcoming' ? (
        <button
          type="button"
          onClick={() => revokeMutation.mutate(g.id)}
          disabled={revokeMutation.isLoading}
          className="flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded-md border border-transparent hover:border-red-200 flex-shrink-0"
          data-testid={`site-grant-revoke-${g.id}`}
        >
          <ShieldOff className="w-3.5 h-3.5" />
          {t('sites.grants.revoke')}
        </button>
      ) : (
        <span className="text-xs text-gray-400 flex-shrink-0">{t(`sites.grants.status.${g.computed_status}`)}</span>
      )}
    </li>
  );

  return (
    <div className="space-y-4" data-testid="site-grant-panel">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Clock className="w-4 h-4 text-purple-600" />
          <span>{t('sites.grants.title')}</span>
        </div>
        <button
          type="button"
          onClick={() => setFormOpen((v) => !v)}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          data-testid="site-grant-toggle-form"
        >
          <UserPlus className="w-4 h-4" />
          {t('sites.grants.confer')}
        </button>
      </div>

      <p className="text-xs text-gray-500">{t('sites.grants.effectNotice')}</p>

      {formOpen && (
        <form onSubmit={submit} className="space-y-3 p-3 rounded-lg border border-gray-200 bg-gray-50" data-testid="site-grant-form">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('sites.grants.form.user')}</label>
            <select
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              data-testid="site-grant-user-select"
            >
              <option value="">{t('sites.grants.form.userPlaceholder')}</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{userLabel(u)}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('sites.grants.form.from')}</label>
              <input
                type="datetime-local"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                data-testid="site-grant-from-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('sites.grants.form.to')}</label>
              <input
                type="datetime-local"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                data-testid="site-grant-to-input"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('sites.grants.form.reason')}</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder={t('sites.grants.form.reasonPlaceholder')}
              data-testid="site-grant-reason-input"
            />
          </div>

          {formError && (
            <div className="flex items-start gap-2 p-2 rounded-md bg-red-50 border border-red-200 text-xs text-red-700" data-testid="site-grant-form-error">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>{formError}</span>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setFormOpen(false)}
              className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              {t('sites.grants.form.cancel')}
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading}
              className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              data-testid="site-grant-submit"
            >
              {t('sites.grants.form.submit')}
            </button>
          </div>
        </form>
      )}

      {grantsQuery.isLoading && (
        <p className="text-gray-500 text-center py-6 text-sm">{t('sites.grants.loading')}</p>
      )}

      {grantsQuery.isError && (
        <p className="text-red-600 text-center py-6 text-sm">
          {t('sites.grants.error', { message: grantsQuery.error?.message || '' })}
        </p>
      )}

      {grantsQuery.data && grants.length === 0 && (
        <p className="text-gray-500 text-center py-6 text-sm" data-testid="site-grant-empty">
          {t('sites.grants.empty')}
        </p>
      )}

      {buckets.active.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">{t('sites.grants.buckets.active')}</h4>
          <ul>{buckets.active.map(renderGrant)}</ul>
        </div>
      )}
      {buckets.upcoming.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">{t('sites.grants.buckets.upcoming')}</h4>
          <ul>{buckets.upcoming.map(renderGrant)}</ul>
        </div>
      )}
      {(buckets.past.length > 0 || buckets.revoked.length > 0) && (
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">{t('sites.grants.buckets.history')}</h4>
          <ul>{[...buckets.past, ...buckets.revoked].map(renderGrant)}</ul>
        </div>
      )}
    </div>
  );
}

export default SiteGrantPanel;
